import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Spinner } from 'reactstrap';
import { InfoCircle } from '@saleshandy/icons';
import { ActivityPropTypes, ActivityType } from './types';
import ActivityLogComponent from '../../molecules/activity-component/activity-log-component';
import { OverlayTooltip, Placement } from '../../overlay';

/**
 *
 * @param onLoadMore - Function to load more activities
 * @param hasMore - Boolean to check if there are more activities to load
 * @param lengthOfPage - Number of activities to load
 * @param activityArr - Array of activities
 * @param wrapperId - Id of the wrapper
 * @param scrollableTarget - Target of the scroll container
 * @param wrapperStyle - Style of the wrapper
 * @returns
 */
const ActivityLog: React.FC<ActivityPropTypes> = ({
  onLoadMore,
  hasMore,
  activityArr,
  wrapperId,
  wrapperStyle,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isLoadingMoreRef = useRef<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const mergedWrapperStyle: React.CSSProperties = useMemo(
    () => ({
      overflowY: 'auto',
      height: '100%',
      // maxHeight: 'calc(100vh - 14rem)', // fallback height to ensure scroll
      ...wrapperStyle,
    }),
    [wrapperStyle],
  );

  const activityTypes = useMemo(() => {
    const regularActivities: ActivityType[] = [];
    const legacyActivities: ActivityType[] = [];

    activityArr.forEach((activityGroup) => {
      const regularActivityItems = activityGroup.activity.filter(
        (item) => item.activityBy,
      );
      const legacyActivityItems = activityGroup.activity.filter(
        (item) => !item.activityBy,
      );

      // Add regular activities if any exist
      if (regularActivityItems.length > 0) {
        regularActivities.push({
          ...activityGroup,
          activity: regularActivityItems,
        });
      }

      // Add legacy activities if any exist
      if (legacyActivityItems.length > 0) {
        legacyActivities.push({
          ...activityGroup,
          activity: legacyActivityItems,
        });
      }
    });

    return {
      regularActivities,
      legacyActivities,
    };
  }, [activityArr]);

  const handleScroll = useCallback(
    (source: 'container' | 'window' = 'container') => {
      const container = scrollContainerRef.current;

      // if (source === 'container' && !container) {
      //   return;
      // }

      if (!hasMore) {
        return;
      }

      if (isLoadingMoreRef.current) {
        return;
      }

      let distanceFromBottom = 0;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      } else {
        const scrollTop = window.scrollY;
        const {scrollHeight} = document.documentElement;
        const clientHeight = window.innerHeight;
        distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      }

      if (distanceFromBottom <= 100 && onLoadMore) {
        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        onLoadMore();
      }
    },
    [activityArr.length, hasMore, onLoadMore],
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    const containerHandler = () => handleScroll('container');
    const windowHandler = () => handleScroll('window');

    if (container) {
      container.addEventListener('scroll', containerHandler);
    }
    window.addEventListener('scroll', windowHandler);
    return () => {
      if (container) {
        container.removeEventListener('scroll', containerHandler);
      }
      window.removeEventListener('scroll', windowHandler);
    };
  }, [handleScroll]);

  // Reset loading flag when new activities arrive or when fetching stops
  useEffect(() => {
    isLoadingMoreRef.current = false;
    setIsLoadingMore(false);
  }, [activityArr, hasMore]);

  return (
    <div
      id={wrapperId}
      ref={scrollContainerRef}
      style={mergedWrapperStyle}
      className="d-flex flex-column flex-grow-1"
    >
      {/* Render regular activities */}
      <div className="d-flex flex-column gap-16 mb-3 single-day-logs">
        {activityTypes?.regularActivities?.map(
          ({ dayText, activity, groupKey }, index) => (
            <ActivityLogComponent
              key={`regular-${index + 1}`}
              dayText={dayText}
              activity={activity}
              groupKey={groupKey}
            />
          ),
        )}

        {/* Render Legacy Activities partition if there are any legacy activities */}
        {activityTypes?.legacyActivities?.length > 0 && (
          <>
            <div className="d-flex flex-column gap-16 mb-3">
              <div className="d-flex align-items-center gap-12">
                <div className="legacy-activities">
                  <span>Legacy Activities</span>
                  <OverlayTooltip
                    text="Legacy activities were created before CRM and don’t support activity type filters."
                    placement={Placement.Bottom}
                  >
                    <InfoCircle
                      width={14}
                      height={14}
                      color="#6b7280"
                      className="cursor-pointer"
                    />
                  </OverlayTooltip>
                </div>
                <div className="flex-grow-1 legacy-activities-divider" />
              </div>

              {activityTypes?.legacyActivities?.map(
                ({ dayText, activity, groupKey }, index) => (
                  <ActivityLogComponent
                    key={`legacy-${index + 1}`}
                    dayText={dayText}
                    activity={activity}
                    groupKey={groupKey}
                  />
                ),
              )}
            </div>
          </>
        )}
      </div>

      {isLoadingMore && hasMore && (
        <div className="text-center mt-5">
          <Spinner color="#1d4ed8" />
        </div>
      )}
    </div>
  );
};

export default memo(ActivityLog);
